import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { getAdminInvoiceById } from "@/services/adminApi";
import { SITE } from "@/config/site";
import type { InvoiceRecord } from "@/types";

export default function AdminInvoicePrint() {
  const { id = "" } = useParams();
  const { token } = useAuth();
  const [invoice, setInvoice] = useState<InvoiceRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdminInvoiceById(id, token)
      .then((inv) => {
        if (active) setInvoice(inv ?? null);
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id, token]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100/60">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background-100/60 px-4 text-center">
        <i className="ri-file-warning-line text-3xl text-secondary-600"></i>
        <p className="text-sm text-foreground-700">This invoice could not be found.</p>
        <Link
          to="/admin/crm/invoices"
          className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i> Back to invoices
        </Link>
      </div>
    );
  }

  const subtotal = invoice.amount;
  const tax = invoice.tax;
  const total = subtotal + tax;

  return (
    <>
      <Seo title={`Invoice ${invoice.number}`} path={`/admin/crm/invoices/${invoice.id}/print`} />

      <div className="flex min-h-screen justify-center bg-background-100/60 px-4 py-8 print:bg-white print:p-0">
        <div className="w-full max-w-3xl">
          <div className="mb-4 flex items-center justify-between print:hidden">
            <Link
              to="/admin/crm/invoices"
              className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> Back
            </Link>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-printer-line"></i> Print / Save as PDF
            </button>
          </div>

          <article className="rounded-lg border border-background-200 bg-background-50 p-8 print:border-0 print:p-0">
            <header className="flex flex-col gap-6 border-b border-background-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-foreground-950">
                  <i className="ri-book-2-line text-xl"></i>
                </span>
                <div>
                  <p className="font-heading text-lg font-extrabold text-foreground-950">{SITE.name}</p>
                  <p className="text-xs text-foreground-500">GSTIN: {SITE.gstin}</p>
                </div>
              </div>
              <div className="sm:text-right">
                <p className="font-heading text-xl font-extrabold text-foreground-950">INVOICE</p>
                <p className="mt-1 font-mono text-xs text-foreground-600">{invoice.number}</p>
                <div className="mt-2"><StatusBadge status={invoice.status} /></div>
              </div>
            </header>

            <section className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-foreground-400">From</p>
                <p className="mt-2 text-sm font-semibold text-foreground-900">{SITE.name}</p>
                <p className="mt-0.5 text-sm text-foreground-600">{SITE.address.line1}</p>
                <p className="text-sm text-foreground-600">{SITE.address.line2}</p>
                <p className="text-sm text-foreground-600">
                  {SITE.address.city}, {SITE.address.region} {SITE.address.postalCode}
                </p>
                <p className="mt-1 text-sm text-foreground-600">{SITE.email} · {SITE.phone}</p>
              </div>
              <div className="sm:text-right">
                <p className="text-[11px] font-bold uppercase tracking-wider text-foreground-400">Billed to</p>
                <p className="mt-2 text-sm font-semibold text-foreground-900">{invoice.contactName}</p>
                <p className="mt-0.5 text-sm text-foreground-600">{invoice.contactEmail}</p>
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:text-right">
                  <span className="text-foreground-500">Order</span>
                  <span className="font-mono text-xs text-foreground-800">{invoice.orderNo}</span>
                  <span className="text-foreground-500">Issued</span>
                  <span className="text-foreground-800">{invoice.issuedAt}</span>
                  <span className="text-foreground-500">Due</span>
                  <span className="text-foreground-800">{invoice.dueAt}</span>
                </div>
              </div>
            </section>

            <section className="mt-8 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-left text-[11px] uppercase tracking-wider text-foreground-500">
                    <th className="pb-2 font-semibold">Description</th>
                    <th className="pb-2 text-center font-semibold">Qty</th>
                    <th className="pb-2 text-right font-semibold">Rate</th>
                    <th className="pb-2 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((it, i) => (
                    <tr key={i} className="border-b border-background-100">
                      <td className="py-3 text-foreground-800">{it.description}</td>
                      <td className="py-3 text-center text-foreground-700">{it.qty}</td>
                      <td className="py-3 text-right text-foreground-700">₹{it.rate.toLocaleString("en-IN")}</td>
                      <td className="py-3 text-right font-semibold text-foreground-900">
                        ₹{(it.qty * it.rate).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="mt-6 flex justify-end">
              <div className="w-full max-w-xs space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500">Subtotal</span>
                  <span className="font-medium text-foreground-800">₹{subtotal.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500">GST (18%)</span>
                  <span className="font-medium text-foreground-800">₹{tax.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex items-center justify-between border-t border-background-200 pt-2">
                  <span className="font-bold text-foreground-950">Total due</span>
                  <span className="font-heading text-lg font-extrabold text-foreground-950">
                    ₹{total.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
            </section>

            <footer className="mt-8 border-t border-background-200 pt-5 text-center text-xs text-foreground-500">
              <p>Thank you for your business. This is a computer-generated invoice and does not require a signature.</p>
              <p className="mt-1">For billing queries, write to {SITE.email}.</p>
            </footer>
          </article>
        </div>
      </div>
    </>
  );
}